import { IAllTasksFilters } from 'views/pages/Worklist/AllWorklist/types.ts';
import { isEqual } from 'lodash';
import { useAllWorklistContext } from 'views/pages/Worklist/AllWorklist/contexts/AllWorklistContext.ts';
import { useEffect, useState } from 'react';
import {
  useAllWorklistMetadataQuery,
  useBuildingsShortQuery,
  usePracticeShortQuery,
  useTaskFilterOptionsQuery,
  useTeamsShortQuery,
  useUserListShortQuery,
} from 'common/cache';
import { SelectChangeEvent } from '@mui/material';
import { ALL_OPTION } from 'common/types/constants.ts';

export type ICardFilters = Pick<
  IAllTasksFilters,
  | 'programs'
  | 'due_date'
  | 'category'
  | 'location_building_ids'
  | 'priorities'
  | 'completion_interval'
  | 'statuses'
  | 'team_id'
  | 'practice_id'
  | 'assignee_id'
  | 'task_names'
  | 'order_types'
  | 'order_categories'
  | 'last_updated_after'
>;

type Option<T> = {
  value: T;
  label: string;
};

const initialFilters: IAllTasksFilters = {
  search: '',
  programs: [],
  due_date: '',
  category: '',
  location_building_ids: [],
  priorities: ['Overdue', 'Urgent', 'High', 'Medium', 'Low'],
  sort_by: 'due_date',
  sort_order: 'ASC',
  page: 1,
  limit: 100,
  statuses: ['Pending', 'In progress'],
  team_id: '',
  assignee_id: '',
  partner_id: '',
  practice_id: '',
  last_updated_after: '',
  task_names: [],
  order_categories: [],
  order_types: [],
  provider_ids: [],
  appliedFilters: 0,
};

type keyOfFilters = keyof IAllTasksFilters;
const ignoreFieldsList = [
  'provider_ids',
  'partner_id',
  'sort_by',
  'sort_order',
  'search',
  'page',
  'limit',
  'appliedFilters',
];

const getAppliedFiltersCount = (filters: IAllTasksFilters) => {
  let count = 0;

  const keysInitial: keyOfFilters[] = Object.keys(
    initialFilters,
  ) as keyOfFilters[];

  keysInitial.forEach(key => {
    if (
      !isEqual(initialFilters[key], filters[key]) &&
      !ignoreFieldsList.includes(key)
    ) {
      count += 1;
    }
  });

  return count;
};

export const useFiltersCard = () => {
  const { handleFilterChange, filters: incompleteFilters } =
    useAllWorklistContext();
  const [filters, setFilters] = useState<ICardFilters>(initialFilters);

  const metadata = useAllWorklistMetadataQuery({
    completed: false,
  });
  const buildingsQuery = useBuildingsShortQuery({});
  const { data: teamData = [] } = useTeamsShortQuery({});
  const { data: practiceData = [] } = usePracticeShortQuery({});
  const { data: userData = [] } = useUserListShortQuery({});
  const { data: taskFilterOptions } = useTaskFilterOptionsQuery({
    completed: false,
  });

  const teamOptions =
    teamData.map(team => ({
      value: team.id,
      label: team.name,
    })) || [];
  const practiceOptions =
    practiceData.map(practice => ({
      value: practice.id,
      label: practice.name,
    })) || [];

  const userOptions =
    userData.map(user => ({
      value: user.id,
      label: user.name,
    })) || [];

  const taskProgramOptions: Option<ICardFilters['programs'][number]>[] = [
    {
      value: 'GUIDE',
      label: `GUIDE (${metadata.data?.task_programs?.GUIDE})`,
    },
    {
      value: 'Pharmacy',
      label: `Pharmacy (${metadata.data?.task_programs?.Pharmacy})`,
    },
    {
      value: 'Optum MA',
      label: `Optum MA (${metadata.data?.task_programs?.['Optum MA']})`,
    },
    {
      value: 'CCM',
      label: `CCM (${metadata.data?.task_programs?.['CCM']})`,
    },
    {
      value: 'other',
      label: `Other (${metadata.data?.task_programs?.other})`,
    },
  ];

  const priorityOptions: Option<ICardFilters['priorities'][number]>[] = [
    {
      value: 'Overdue',
      label: `Overdue (${metadata.data?.priorities?.overdue})`,
    },
    {
      value: 'Urgent',
      label: `Urgent (${metadata.data?.priorities?.urgent})`,
    },
    {
      value: 'High',
      label: `High (${metadata.data?.priorities?.high})`,
    },
    {
      value: 'Medium',
      label: `Medium (${metadata.data?.priorities?.medium})`,
    },
    {
      value: 'Low',
      label: `Low (${metadata.data?.priorities?.low ?? ' '})`,
    },
  ];

  const statusOptions: Option<ICardFilters['statuses'][number]>[] = [
    {
      value: 'Pending',
      label: `Not Started (${metadata.data?.statuses?.pending})`,
    },
    {
      value: 'In progress',
      label: `In progress (${metadata.data?.statuses?.in_progress})`,
    },
  ];

  const categoryOptions = metadata.data?.categories || [];

  const locationOptions =
    buildingsQuery.data?.map(building => ({
      value: building.id,
      label: building.name,
    })) || [];

  const handleTeamOnChange = (event: SelectChangeEvent<string>) => {
    setFilters(prev => ({
      ...prev,
      team_id: event.target.value,
    }));
  };

  const handlePracticeOnChange = (event: SelectChangeEvent<string>) => {
    setFilters(prev => ({
      ...prev,
      practice_id: event.target.value,
    }));
  };

  const handleAssigneeOnChange = (event: SelectChangeEvent<string>) => {
    setFilters(prev => ({
      ...prev,
      assignee_id: event.target.value,
    }));
  };

  const handlePriorityOnChange = (
    key: ICardFilters['priorities'][number] | '',
  ) => {
    if (key === ALL_OPTION.value) {
      setFilters(prev => ({
        ...prev,
        priorities:
          priorityOptions.length === filters.priorities.length
            ? []
            : priorityOptions.map(option => option.value),
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        priorities: filters.priorities.includes(key)
          ? prev.priorities.filter(option => option !== key)
          : [...prev.priorities, key],
      }));
    }
  };

  const handleStatusOnChange = (key: ICardFilters['statuses'][number] | '') => {
    if (key === ALL_OPTION.value) {
      setFilters(prev => ({
        ...prev,
        statuses:
          statusOptions.length === filters.statuses.length
            ? []
            : statusOptions.map(option => option.value),
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        statuses: filters.statuses.includes(key)
          ? prev.statuses.filter(option => option !== key)
          : [...prev.statuses, key],
      }));
    }
  };

  const handleDueDateOnChange = (newDate: string) => {
    setFilters(prev => ({
      ...prev,
      due_date: newDate,
    }));
  };

  const handleCategoryOnChange = (e: SelectChangeEvent<string>) => {
    setFilters(prev => ({
      ...prev,
      category: e.target.value as string,
    }));
  };

  const handleLocationOnChange = (values: string[]) => {
    setFilters(prev => ({
      ...prev,
      location_building_ids: values,
    }));
  };

  const handleClearAll = () => {
    handleFilterChange({
      ...initialFilters,
      provider_ids: incompleteFilters.provider_ids,
      partner_id: incompleteFilters.partner_id,
      search: incompleteFilters.search,
      statuses: initialFilters.statuses,
    });
    setFilters(initialFilters);
  };

  const handleApply = () => {
    handleFilterChange({
      ...initialFilters,
      completion_interval: undefined,
      programs: filters.programs,
      due_date: filters.due_date,
      priorities: filters.priorities,
      last_updated_after: filters.last_updated_after,
      statuses: filters.statuses,
      category: filters.category,
      team_id: filters.team_id,
      practice_id: filters.practice_id,
      assignee_id: filters.assignee_id,
      location_building_ids: filters.location_building_ids,
      task_names: filters.task_names,
      order_categories: filters.order_categories,
      order_types: filters.order_types,
      provider_ids: incompleteFilters.provider_ids,
      partner_id: incompleteFilters.partner_id,
      search: incompleteFilters.search,
      appliedFilters: getAppliedFiltersCount({
        ...filters,
        provider_ids: [],
      }),
    });
  };

  const handleLastUpdatedDateOnChange = (newDate: string) => {
    setFilters(prev => ({
      ...prev,
      last_updated_after: newDate,
    }));
  };

  const handleTaskNameOnChange = (newValues: string[]) => {
    const value = newValues as ICardFilters['task_names'];
    setFilters(prev => ({
      ...prev,
      task_names: value,
    }));
  };

  const handleProgramOnChange = (newValues: string[]) => {
    const value = newValues as ICardFilters['programs'];
    setFilters(prev => ({
      ...prev,
      programs: value,
    }));
  };

  const handleOrderCategoryOnChange = (newValues: string[]) => {
    const value = newValues as ICardFilters['order_categories'];
    setFilters(prev => ({
      ...prev,
      order_categories: value,
    }));
  };

  const handleOrderTypeOnChange = (newValues: string[]) => {
    const value = newValues as ICardFilters['order_types'];
    setFilters(prev => ({
      ...prev,
      order_types: value,
    }));
  };

  useEffect(() => {
    setFilters({ ...incompleteFilters });
  }, [incompleteFilters]);

  return {
    handleClearAll,
    handleApply,
    filters,
    taskProgramOptions,
    priorityOptions: priorityOptions.map(o => ({
      value: o.value,
      label: o.label,
    })),
    categoryOptions: categoryOptions.map(o => ({
      value: o,
      label: o,
    })),
    locationOptions,
    userOptions,
    teamOptions,
    practiceOptions,
    handleTeamOnChange,
    handlePracticeOnChange,
    handleAssigneeOnChange,
    handleProgramOnChange,
    handleDueDateOnChange,
    handleCategoryOnChange,
    handleLocationOnChange,
    handlePriorityOnChange,
    statusOptions,
    handleStatusOnChange,
    resetIsDisabled: isEqual(filters, initialFilters),
    handleTaskNameOnChange,
    handleOrderCategoryOnChange,
    handleOrderTypeOnChange,
    handleLastUpdatedDateOnChange,
    taskNameOptions: taskFilterOptions?.task_names.map(o => ({
      value: o,
      label: o,
    })),
    orderTypeOptions: taskFilterOptions?.order_types
      .filter(item => item !== 'Completed task')
      .map(o => ({
        value: o,
        label: o,
      })),
    orderCategoryOptions: taskFilterOptions?.order_categories.map(o => ({
      value: o,
      label: o,
    })),
  };
};
